import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { Briefcase, MapPin, Phone, Mail, Building2, ArrowLeft, DollarSign, Clock, CheckCircle, Send, Share2, Copy, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/jobs/${jobId}`;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link skopiowany!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Nie udało się skopiować linku');
    }
  };

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await fetch(`${API}/jobs/${jobId}`);
        if (res.ok) {
          setJob(await res.json());
        } else {
          toast.error('Nie znaleziono oferty');
          navigate('/jobs');
        }
      } catch (err) {
        console.error('Error:', err);
        toast.error('Błąd podczas ładowania');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId, navigate]);

  const handleApply = async () => {
    if (!user) {
      toast.error('Zaloguj się, aby aplikować');
      navigate('/login');
      return;
    }
    
    if (!message.trim()) {
      toast.error('Napisz wiadomość do pracodawcy');
      return;
    }

    setApplying(true);
    try {
      const res = await fetch(`${API}/jobs/${jobId}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message })
      });

      if (res.ok) {
        toast.success('Aplikacja wysłana!');
        setMessage('');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Błąd podczas wysyłania');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    } finally {
      setApplying(false);
    }
  };

  const formatSalary = (min, max, type) => {
    const format = (n) => new Intl.NumberFormat('pl-PL').format(n);
    const suffix = type === 'hourly' ? '/godz.' : type === 'daily' ? '/dzień' : '/mies.';
    
    if (max && max !== min) {
      return `${format(min)} - ${format(max)} PLN${suffix}`;
    }
    return `${format(min)} PLN${suffix}`;
  };

  const employmentTypeNames = {
    full_time: 'Pełny etat',
    part_time: 'Część etatu',
    contract: 'Umowa zlecenie',
    b2b: 'B2B / Samozatrudnienie',
    temporary: 'Praca tymczasowa',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="job-details-page">
      <SEO 
        title={`${job.title} - ${job.company_name}`}
        description={`${job.title} w ${job.location}. Wynagrodzenie: ${formatSalary(job.salary_min, job.salary_max, job.salary_type)}. ${job.description?.substring(0, 100)}`}
        canonical={`/jobs/${jobId}`}
        image={job.images?.[0] || undefined}
        type="article"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/jobs')} 
            className="text-violet-100 hover:text-white hover:bg-violet-500/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Powrót do listy
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    {job.employer_logo ? (
                      <img src={job.employer_logo} alt={job.company_name} className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      <Briefcase className="w-8 h-8 text-violet-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">{job.title}</h1>
                    <p className="text-lg text-slate-600 flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      {job.company_name || 'Pracodawca'}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge variant="outline" className="border-violet-200 text-violet-700">
                        {employmentTypeNames[job.employment_type] || job.employment_type}
                      </Badge>
                      <Badge variant="outline" className="border-green-200 text-green-700">
                        <DollarSign className="w-3 h-3 mr-1" />
                        {formatSalary(job.salary_min, job.salary_max, job.salary_type)}
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleShare}
                    className="flex-shrink-0"
                    data-testid="share-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                        Skopiowano!
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4 mr-2" />
                        Udostępnij
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>Opis stanowiska</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap">{job.description}</p>
              </CardContent>
            </Card>

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Wymagania</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {job.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{req}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Benefits */}
            {job.benefits && job.benefits.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Oferujemy</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {job.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Key Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Informacje</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-violet-500" />
                  <div>
                    <p className="text-sm text-slate-500">Lokalizacja</p>
                    <p className="font-medium">{job.location}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-sm text-slate-500">Wynagrodzenie</p>
                    <p className="font-medium text-green-600">{formatSalary(job.salary_min, job.salary_max, job.salary_type)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-violet-500" />
                  <div>
                    <p className="text-sm text-slate-500">Typ zatrudnienia</p>
                    <p className="font-medium">{employmentTypeNames[job.employment_type]}</p>
                  </div>
                </div>
                
                {job.experience_required && (
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-violet-500" />
                    <div>
                      <p className="text-sm text-slate-500">Doświadczenie</p>
                      <p className="font-medium">{job.experience_required}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Kontakt</CardTitle>
              </CardHeader>
              <CardContent>
                {job.contact_phone && (
                  <a href={`tel:${job.contact_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-violet-600 mb-2">
                    <Phone className="w-4 h-4" />
                    {job.contact_phone}
                  </a>
                )}
                
                {job.contact_email && (
                  <a href={`mailto:${job.contact_email}`} className="flex items-center gap-2 text-slate-600 hover:text-violet-600">
                    <Mail className="w-4 h-4" />
                    {job.contact_email}
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Apply Form */}
            <Card className="border-2 border-violet-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Send className="w-5 h-5 text-violet-500" />
                  Aplikuj na stanowisko
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Przedstaw się i napisz dlaczego chcesz pracować na tym stanowisku..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className="mb-4"
                  data-testid="apply-message"
                />
                <Button 
                  onClick={handleApply} 
                  disabled={applying}
                  className="w-full bg-violet-500 hover:bg-violet-600"
                  data-testid="apply-btn"
                >
                  {applying ? 'Wysyłanie...' : 'Wyślij aplikację'}
                </Button>
                
                {!user && (
                  <p className="text-sm text-slate-500 text-center mt-2">
                    <a href="/login" className="text-violet-600 hover:underline">Zaloguj się</a> aby aplikować
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
